/**
 * The top-level state machine and the run sub-states (TASKS T45, SPEC §3).
 *
 * Pure: no DOM, no clock, no randomness, no store. The machine says which transitions are legal and
 * nothing else — it does not draw items, grade answers, or write to disk. Everything it governs is
 * a fact about *sequence*, and sequence is where two of the pedagogical invariants actually live:
 *
 *   - **`awaiting` is the only state that accepts answer input** (SPEC §3). Expressed here as
 *     `acceptsAnswer()`, so no card has to decide for itself whether a keystroke counts.
 *   - **Every transition out of `grading` writes the attempt to the store before feedback renders**
 *     (SPEC §3, §10). Expressed here by there being exactly one event that leaves `grading`, and it
 *     is called `bank`. There is no path from `grading` to `feedback` that is not named after
 *     persisting the attempt, so "we forgot to save it" is not a state this machine can reach.
 *
 * Losing a completed review to a closed tab is a defect (SPEC §12, DoD-5). This file is the half of
 * that guarantee that a test can read.
 */

import type { Phase, RunPhase } from './gamestate';

export interface MachineState {
  phase: Phase;
  runPhase: RunPhase;
}

/**
 * Events, split by the layer they act on.
 *
 * Run sub-events are rejected outright unless `phase === 'run'`, so a stray `submit` arriving from
 * a card that outlived its phase cannot advance anything.
 */
export type MachineEvent =
  // ---- top level -----------------------------------------------------------------------------
  /** The store is loaded, the queue is computed, speech has been feature-detected. */
  | { type: 'boot-complete' }
  /** The one-off conversion demonstration is due, so the first run waits behind it. */
  | { type: 'demo-start' }
  /** It has been read. The run begins, exactly as `run-start` would have begun it. */
  | { type: 'demo-done' }
  /** Same, but no baseline has ever been recorded, so setup comes first (T46b). */
  | { type: 'setup-required' }
  /** The baseline is **on disk**. Named for the write, like `bank`, for the same reason. */
  | { type: 'setup-complete' }
  | { type: 'run-start' }
  /** The timer expired or the learner ended the run. Legal from every run sub-state. */
  | { type: 'run-end' }
  | { type: 'reveal-dismiss' }
  | { type: 'tracker-open' }
  | { type: 'tracker-close' }
  | { type: 'log-open' }
  | { type: 'log-close' }
  | { type: 'vetting-open' }
  | { type: 'vetting-close' }
  // ---- inside a run --------------------------------------------------------------------------
  /**
   * This item has never been asked before, so it is shown first (T59c).
   *
   * Unscored, and legal only out of `drawing`: a first exposure is a property of the draw, never
   * something the learner can ask for once a card is up — that would be a scaffold with no cost.
   */
  | { type: 'teach' }
  /** The round builder produced an `(item, mode)` pair. */
  | { type: 'present' }
  /** The card is on screen and ready for input. */
  | { type: 'ready' }
  /** The learner committed an answer, or declared "no sé". */
  | { type: 'submit' }
  /**
   * The graded attempt is **persisted**. The only way out of `grading`, and named for the reason
   * why: feedback must never render against an attempt that is not yet on disk.
   */
  | { type: 'bank' }
  /** Second failure: force the self-reference encoding step before this item can requeue. */
  | { type: 'encode' }
  | { type: 'encoded' }
  /** Move on to the next draw. */
  | { type: 'next' }
  /** Nothing left to draw. The run does not end — the timer still owns that. */
  | { type: 'exhausted' };

export type EventType = MachineEvent['type'];

// ---------------------------------------------------------------------------------------------
// The tables
// ---------------------------------------------------------------------------------------------

/**
 * Top-level transitions, exactly as tabulated in SPEC §3.
 *
 * `tracker` is reachable from both `entry` and `reveal` and returns to `entry`. There is no path
 * from `run` to anything but `reveal`: a run in progress is not a menu.
 */
const PHASE_TABLE: Readonly<Record<Phase, Partial<Record<EventType, MachineState>>>> = {
  boot: {
    'boot-complete': { phase: 'entry', runPhase: 'idle' },
    'setup-required': { phase: 'setup', runPhase: 'idle' },
  },
  /**
   * First launch only, and there is exactly one way out of it.
   *
   * No `run-start` and no `tracker-open`: this card is answered — with blanks if he likes — before
   * anything else, because the pre-game baseline is unrecoverable once play has started. It is also
   * unreachable from anywhere else. Editing the baseline later happens in the tracker against the
   * same stored profile, not by returning here, so nothing can re-prompt (see DECISIONS D-102).
   */
  setup: {
    'setup-complete': { phase: 'entry', runPhase: 'idle' },
  },
  entry: {
    'run-start': { phase: 'run', runPhase: 'drawing' },
    'demo-start': { phase: 'demo', runPhase: 'idle' },
    'tracker-open': { phase: 'tracker', runPhase: 'idle' },
  },
  /**
   * The demonstration, and there is exactly one way out of it: into the run it delayed.
   *
   * No `tracker-open`, because it is not a place to be — it is one card between tapping start and
   * the first draw. And no path back to `entry`, so it cannot be re-entered and re-read: it is
   * shown once, and `demoSeenAt` on the profile is written before this transition fires.
   */
  demo: {
    'demo-done': { phase: 'run', runPhase: 'drawing' },
  },
  run: {
    'run-end': { phase: 'reveal', runPhase: 'idle' },
  },
  reveal: {
    'reveal-dismiss': { phase: 'entry', runPhase: 'idle' },
    'tracker-open': { phase: 'tracker', runPhase: 'idle' },
  },
  tracker: {
    'tracker-close': { phase: 'entry', runPhase: 'idle' },
    'log-open': { phase: 'log', runPhase: 'idle' },
    'vetting-open': { phase: 'vetting', runPhase: 'idle' },
  },
  log: {
    'log-close': { phase: 'tracker', runPhase: 'idle' },
  },
  vetting: {
    'vetting-close': { phase: 'tracker', runPhase: 'idle' },
  },
};

/**
 * The per-item loop inside a run (SPEC §3, extended by T59c):
 *
 * `drawing → [teaching] → presenting → awaiting → grading → feedback → [encoding] → drawing`
 *
 * Note what is *absent*. There is no edge from `awaiting` back to `awaiting`, so a second
 * submission cannot re-grade the same card; no edge from `feedback` to `awaiting`, so feedback is
 * not a retry prompt; and no edge into `encoding` except from `feedback`, because the encoding step
 * is a consequence of a graded second failure and never a thing the learner opts into.
 */
const RUN_TABLE: Readonly<Record<RunPhase, Partial<Record<EventType, RunPhase>>>> = {
  idle: { present: 'presenting' },
  drawing: { teach: 'teaching', present: 'presenting', exhausted: 'idle' },
  // The only way out is `present`: having been shown the item, the learner is then asked for it.
  // There is no path from `teaching` back to `drawing`, so a first exposure cannot be used to skip.
  teaching: { present: 'presenting' },
  presenting: { ready: 'awaiting' },
  awaiting: { submit: 'grading' },
  grading: { bank: 'feedback' },
  feedback: { encode: 'encoding', next: 'drawing' },
  encoding: { encoded: 'drawing' },
};

export function initialState(): MachineState {
  return { phase: 'boot', runPhase: 'idle' };
}

/**
 * Apply an event. Returns the next state, or `null` if the transition is not legal.
 *
 * Pure and total: every state/event pair has an answer, and the answer for most of them is "no".
 */
export function transition(state: MachineState, event: MachineEvent): MachineState | null {
  const top = PHASE_TABLE[state.phase][event.type];
  if (top) return top;

  // Run sub-events are meaningless outside a run, including the ones whose names would otherwise
  // read as legal — a `submit` arriving while the tracker is open must not grade anything.
  if (state.phase !== 'run') return null;

  const runPhase = RUN_TABLE[state.runPhase][event.type];
  if (!runPhase) return null;
  return { phase: 'run', runPhase };
}

export function canTransition(state: MachineState, event: MachineEvent): boolean {
  return transition(state, event) !== null;
}

// ---------------------------------------------------------------------------------------------
// Predicates the rest of the app asks instead of comparing strings
// ---------------------------------------------------------------------------------------------

/**
 * Is this the one state that accepts an answer? (SPEC §3.)
 *
 * Every input path — typed, tiled, spoken, gamepad — asks this rather than checking `runPhase`
 * itself. A keystroke that lands during `grading` or `feedback` is not an answer, and a card that
 * decided that for itself would eventually decide it differently.
 */
export function acceptsAnswer(state: MachineState): boolean {
  return state.phase === 'run' && state.runPhase === 'awaiting';
}

/** Is a run in progress? True through every sub-state, including `feedback` and `encoding`. */
export function isRunning(state: MachineState): boolean {
  return state.phase === 'run';
}

/**
 * Is the learner mid-item — drawn, presented, or answering, but not yet banked?
 *
 * This is what "the in-flight item is allowed to complete" turns on when the timer expires
 * (SPEC §1), and what DoD-5 means by the item that must *not* survive a reload.
 */
export function hasItemInFlight(state: MachineState): boolean {
  if (state.phase !== 'run') return false;
  return (
    state.runPhase === 'presenting' ||
    state.runPhase === 'awaiting' ||
    state.runPhase === 'grading'
  );
}

/**
 * Is the learner part-way through an item, including reading its feedback?
 *
 * This, not `hasItemInFlight`, is what the expiring timer waits for. "The in-flight item is allowed
 * to complete" (SPEC §1) includes reading why the answer was wrong — feedback that names the error
 * is the pedagogically active part of the item (BRIEF <spanish_correctness>), and closing the run
 * over it would make the last item of every run the one the learner learns nothing from.
 *
 * Kept separate from `hasItemInFlight` because that predicate answers a different question — what a
 * reload may destroy (DoD-5) — and by `feedback` the attempt is already on disk.
 */
export function isMidItem(state: MachineState): boolean {
  if (state.phase !== 'run') return false;
  // `teaching` is in here for the same reason `feedback` is: it is the part of the item where the
  // learning happens. Closing the run over a word he has just been shown and never once been asked
  // for would be the worst moment of all to stop.
  return (
    hasItemInFlight(state) ||
    isEncoding(state) ||
    isTeaching(state) ||
    state.runPhase === 'feedback'
  );
}

/** The unscored first-exposure step (T59c). */
export function isTeaching(state: MachineState): boolean {
  return state.phase === 'run' && state.runPhase === 'teaching';
}

/** The unscored self-reference encoding step (invariant 5). */
export function isEncoding(state: MachineState): boolean {
  return state.phase === 'run' && state.runPhase === 'encoding';
}

// ---------------------------------------------------------------------------------------------
// The stateful wrapper
// ---------------------------------------------------------------------------------------------

export interface RejectedTransition {
  from: MachineState;
  event: EventType;
}

export type MachineListener = (state: MachineState, event: MachineEvent) => void;

/**
 * Holds the current state and applies events.
 *
 * `dispatch` returns whether the transition happened and **does not throw** on an illegal one. An
 * illegal transition is a bug, but the safe response to it is to do nothing: the alternative is
 * killing a run — and with it the learner's remaining minutes — over a stray event. Rejections are
 * recorded instead, and the E2E suite asserts the list is empty after a full run, so "do nothing"
 * never quietly becomes "do nothing, forever, for a reason nobody noticed".
 */
export class Machine {
  private state: MachineState;
  private readonly listeners: MachineListener[] = [];
  private readonly rejects: RejectedTransition[] = [];

  constructor(initial: MachineState = initialState()) {
    this.state = initial;
  }

  get(): MachineState {
    return this.state;
  }

  /** Every illegal transition attempted since construction. Asserted empty by tests. */
  rejected(): readonly RejectedTransition[] {
    return this.rejects;
  }

  dispatch(event: MachineEvent): boolean {
    const next = transition(this.state, event);
    if (!next) {
      this.rejects.push({ from: this.state, event: event.type });
      return false;
    }
    this.state = next;
    for (const listener of this.listeners) listener(next, event);
    return true;
  }

  /** Subscribe to state changes. Returns an unsubscribe function. */
  subscribe(listener: MachineListener): () => void {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index >= 0) this.listeners.splice(index, 1);
    };
  }

  reset(to: MachineState = initialState()): void {
    this.state = to;
    this.rejects.length = 0;
  }

  // Convenience predicates, so callers do not have to reach for `get()` and then a free function.
  acceptsAnswer(): boolean {
    return acceptsAnswer(this.state);
  }

  isRunning(): boolean {
    return isRunning(this.state);
  }

  hasItemInFlight(): boolean {
    return hasItemInFlight(this.state);
  }

  isMidItem(): boolean {
    return isMidItem(this.state);
  }
}
